import { useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { modifyLeftEyeImages, modifyRightEyeImages, modifyLeftEyeCurrentImage, modifyRightEyeCurrentImage, patchImageInEye } from '../redux/images'
import { useUploadImage } from './NewCase/Service/useUploadImage'
import { useGetQuality } from './NewCase/Service/useGetQuality'
import { useRemoveImage } from './NewCase/Service/useRemoveImage'
import { useRestoreImage } from './useRestoreImage'

const MAX_FILES_LENGTH = 7

export function useImagesSelector ({ whichEye }) {
  const { leftEye, rightEye } = useSelector(state => state.images)
  const { uploadImage } = useUploadImage()
  const { getStatus, getQualityImage } = useGetQuality()
  const { removeImage } = useRemoveImage()
  const { restoreImageMetadata, getOriginalImageUrl } = useRestoreImage()

  const dispatch = useDispatch()
  const PROVISIONAL_ID_IMAGES = {
  }
  const EYE_REFERENCE = {
    left: {
      currentSelectedImage: leftEye.currentImage,
      images: leftEye.images,
      handleChangeImages: modifyLeftEyeImages,
      setCurrentSelectedImage: modifyLeftEyeCurrentImage
    },
    right: {
      currentSelectedImage: rightEye.currentImage,
      images: rightEye.images,
      handleChangeImages: modifyRightEyeImages,
      setCurrentSelectedImage: modifyRightEyeCurrentImage
    }
  }

  const [nextId, setNextId] = useState(1)
  const inputFileRef = useRef(null)
  const restoreIdRef = useRef(0)

  const resetInputRef = () => {
    inputFileRef.current.value = ''
  }

  const handleOpenFileExplorer = () => {
    inputFileRef.current.click()
  }
  const loadingFiles = (files) => {
    const { images, handleChangeImages } = EYE_REFERENCE[whichEye]
    const alreadyStoredImages = images.filter(file => file.image !== null)
    const imagesSelected = []
    let localNextId = nextId

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const image = URL.createObjectURL(file)
      imagesSelected.push({ id: localNextId, image, name: file.name, status: 'loading' })
      PROVISIONAL_ID_IMAGES[file] = localNextId
      localNextId++
    }

    let newImages = [...alreadyStoredImages, ...imagesSelected].slice(0, MAX_FILES_LENGTH)

    for (let i = newImages.length; i < MAX_FILES_LENGTH; i++) {
      newImages.push({ id: localNextId, image: null, name: null, status: 'none' })
      localNextId++
    }

    newImages = newImages.map((image, index) => {
      return { ...image, position: index + 1 }
    })

    setNextId(localNextId)
    dispatch(handleChangeImages(newImages))
  }
  const uploadFiles = async (files) => {
    const { images, handleChangeImages } = EYE_REFERENCE[whichEye]
    const alreadyStoredImages = images.filter(file => file.image !== null)

    const imagesSelected = []
    let localNextId = nextId

    const uploadFileAndSetImage = async (file) => {
      try {
        const response = await uploadImage(file, whichEye)

        if (response) {
          await getQualityImage(await response.idImagen)
          const id = response.idImagen
          const image = URL.createObjectURL(file)
          const name = file.name
          return { id, image, name }
        }
      } catch (error) {
      }
    }

    const updateImagesStorage = (file, id, finalImage, name, status, iluminacion, contraste, enfoque) => {
      const IMAGE_ID = PROVISIONAL_ID_IMAGES[file]
      const img = alreadyStoredImages.find(image => image.id === IMAGE_ID)

      if (img) {
        const newImages = alreadyStoredImages.map(image => {
          if (image.id === IMAGE_ID) {
            return { ...image, id, finalImage, name, status, iluminacion, contraste, enfoque }
          }
          return image
        })

        dispatch(handleChangeImages(newImages))
      }
    }
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const { id, image, name } = await uploadFileAndSetImage(file)
      const { iluminacion, contraste, enfoque, status } = await getStatus(id)
      imagesSelected.push({ id, image, name, status, iluminacion, contraste, enfoque })
      updateImagesStorage(file, id, image, name, status, iluminacion, contraste, enfoque)
      localNextId++
    }

    let newImages = [...alreadyStoredImages, ...imagesSelected].slice(0, MAX_FILES_LENGTH)

    for (let i = newImages.length; i < MAX_FILES_LENGTH; i++) {
      newImages.push({ id: localNextId, image: null, name: null, status: 'none' })
      localNextId++
    }

    newImages = newImages.map((image, index) => {
      return { ...image, position: index + 1 }
    })

    setNextId(localNextId)
    dispatch(handleChangeImages(newImages))
  }

  const handleUploadImage = (event) => {
    try {
      const { files } = event.target
      if (files.length) {
        const filesArray = [...files]
        loadingFiles(filesArray)
        uploadFiles(filesArray)
      }
    } catch (err) {
    } finally {
      resetInputRef()
    }
  }

  const handleRestoreImages = (study) => {
    const studyImages = (study && study.imagenes) || []
    if (!studyImages.length) return

    const currentRestoreId = ++restoreIdRef.current

    const restore = async () => {
      // Fase 1: fetch de metadata de todas las imágenes en paralelo para conocer asignación de ojo
      const metadataResults = await Promise.all(
        studyImages.map(imgRef => restoreImageMetadata(imgRef.idHash))
      )

      if (currentRestoreId !== restoreIdRef.current) return

      const validMetadata = metadataResults.filter(Boolean)
      if (!validMetadata.length) return

      // Agrupar por ojo preservando el orden original
      const rightEyeMeta = validMetadata.filter(m => m.eyeKey === 'rightEye')
      const leftEyeMeta = validMetadata.filter(m => m.eyeKey === 'leftEye')

      // Construir array de slots pending para un ojo dado
      const buildPendingSlots = (eyeMeta) => {
        const slots = eyeMeta.map((m, idx) => ({
          id: m.id,
          image: null,
          heatMapImage: null,
          restoreState: 'pending',
          iluminacion: m.iluminacion,
          contraste: m.contraste,
          enfoque: m.enfoque,
          probabilidad: m.probabilidad,
          riesgoIA: m.riesgoIA,
          status: m.status,
          manualStatus: m.manualStatus,
          position: idx + 1
        }))
        for (let i = slots.length; i < MAX_FILES_LENGTH; i++) {
          slots.push({ id: -(i + 1), image: null, name: null, status: 'none', position: i + 1 })
        }
        return slots
      }

      // Fase 2: inicializar slots pending en Redux por ojo
      if (rightEyeMeta.length > 0) dispatch(modifyRightEyeImages(buildPendingSlots(rightEyeMeta)))
      if (leftEyeMeta.length > 0) dispatch(modifyLeftEyeImages(buildPendingSlots(leftEyeMeta)))

      if (currentRestoreId !== restoreIdRef.current) return

      // Fase 3: fetch de blobs en paralelo, despachar cada imagen al resolverse
      let rightEyeAutoSelected = false
      let leftEyeAutoSelected = false

      await Promise.all(
        validMetadata.map(async (meta) => {
          dispatch(patchImageInEye(meta.eyeKey, meta.id, { restoreState: 'loading' }))
          const imageUrl = await getOriginalImageUrl(meta.id)

          if (currentRestoreId !== restoreIdRef.current) {
            if (imageUrl) URL.revokeObjectURL(imageUrl)
            return
          }

          if (imageUrl) {
            dispatch(patchImageInEye(meta.eyeKey, meta.id, { image: imageUrl, restoreState: 'ready' }))
            if (meta.eyeKey === 'rightEye' && !rightEyeAutoSelected) {
              rightEyeAutoSelected = true
              dispatch(modifyRightEyeCurrentImage({
                id: meta.id,
                image: imageUrl,
                heatMapImage: null,
                restoreState: 'ready',
                iluminacion: meta.iluminacion,
                contraste: meta.contraste,
                enfoque: meta.enfoque,
                probabilidad: meta.probabilidad,
                riesgoIA: meta.riesgoIA,
                status: meta.status,
                manualStatus: meta.manualStatus
              }))
            }
            if (meta.eyeKey === 'leftEye' && !leftEyeAutoSelected) {
              leftEyeAutoSelected = true
              dispatch(modifyLeftEyeCurrentImage({
                id: meta.id,
                image: imageUrl,
                heatMapImage: null,
                restoreState: 'ready',
                iluminacion: meta.iluminacion,
                contraste: meta.contraste,
                enfoque: meta.enfoque,
                probabilidad: meta.probabilidad,
                riesgoIA: meta.riesgoIA,
                status: meta.status,
                manualStatus: meta.manualStatus
              }))
            }
          } else {
            dispatch(patchImageInEye(meta.eyeKey, meta.id, { restoreState: 'error' }))
          }
        })
      )
    }

    restore()
  }

  const handleUploadDroppedImages = (files) => {
    try {
      if (files.length) {
        loadingFiles(files)
        uploadFiles(files)
      }
    } catch (err) {
    }
  }

  const handleDropImages = (event) => {
    event.preventDefault()

    const files = []
    if (event.dataTransfer.items) {
      for (let i = 0; i < event.dataTransfer.items.length; i++) {
        if (event.dataTransfer.items[i].kind === 'file') {
          const file = event.dataTransfer.items[i].getAsFile()
          files.push(file)
        }
      }
    }
    handleUploadDroppedImages(files)
  }

  const handleDragImages = (event) => {
    event.preventDefault()
  }

  const handleRemoveImage = () => {
    const { images, currentSelectedImage, setCurrentSelectedImage, handleChangeImages } = EYE_REFERENCE[whichEye]
    removeImage(currentSelectedImage.id)

    const currentIndex = images.findIndex(elem => elem.id === currentSelectedImage.id)
    let newUploadedFiles = images.filter(file => file.image !== null)
      .filter(file => file.id !== currentSelectedImage.id)
      .map((image, index) => {
        return { ...image, position: index + 1 }
      })
    const nextCurrentSelectImageIndex = currentIndex === newUploadedFiles.length ? newUploadedFiles.length - 1 : currentIndex
    const newCurrentImageSelected = newUploadedFiles[nextCurrentSelectImageIndex]?.image ? newUploadedFiles[nextCurrentSelectImageIndex] : null

    let localNextId = nextId
    for (let i = newUploadedFiles.length; i < MAX_FILES_LENGTH; i++) {
      newUploadedFiles.push({ id: localNextId, image: null, name: null })
      localNextId++
    }

    newUploadedFiles = newUploadedFiles.map((image, index) => {
      return { ...image, position: index + 1 }
    })

    setNextId(localNextId)
    dispatch(handleChangeImages(newUploadedFiles))
    dispatch(setCurrentSelectedImage(newCurrentImageSelected))
  }

  return {
    inputFileRef,
    handleOpenFileExplorer,
    handleRestoreImages,
    handleUploadImage,
    handleDragImages,
    handleDropImages,
    handleRemoveImage
  }
}

export default useImagesSelector
